import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  aiPrSummaryVsStandupPath,
  asyncStandupPath,
  dailyStandupCostPath,
  digestExamplesPath,
  githubDigestPath,
  meetingWastePath,
  publicChangelogOwnerPath,
  publicChangelogRepositoryPath,
  rootPath,
  signInPath,
  standupBotsPath,
} from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const CAMPAIGN = 'utm_source=replace-standups&utm_campaign=workflow'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

const ROWS = [
  {
    label: 'Who writes the update',
    standup: 'Each person, out loud or in a form',
    digest: 'Nobody. The repositories do',
  },
  {
    label: 'Source of truth',
    standup: 'What someone remembers',
    digest: 'Commits and pull requests that landed',
  },
  {
    label: 'Cadence',
    standup: 'A meeting on the calendar',
    digest: 'Daily (previous 24 hours) or weekly',
  },
  {
    label: 'Where it lands',
    standup: 'The call, or a bot thread',
    digest: 'Email and a Slack channel',
  },
  {
    label: 'Work that is not in git',
    standup: 'Someone can still say it',
    digest: 'Invisible, unless it was committed',
  },
] as const

function campaignPath(path: string) {
  return `${path}?${CAMPAIGN}`
}

function TextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className={linkClass}>
      {children}
    </Link>
  )
}

export function ReplaceStandupsArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">Replace standups</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          A standup asks people to remember what they shipped. A GitHub activity digest reads the repositories you
          pick and posts that summary to Slack and email.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 text-center sm:p-8">
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
          <TextLink href={rootPath()}>Git Digest</TextLink> is the team product for that job. Sign in with GitHub,
          choose the repos, and let the digest replace the status round.
        </p>
      </Card>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">From GitHub to Slack and email</h2>
        <ol className="text-muted-foreground list-decimal space-y-3 pl-5 text-base leading-relaxed">
          <li>Sign in with GitHub and open the workspace.</li>
          <li>Pick the repositories that should be digested. Only those repos are included.</li>
          <li>
            Choose daily or weekly, the organization timezone, and a delivery hour. A daily digest covers the previous
            24 hours. A weekly digest covers the past week and is prepared on Mondays.
          </li>
          <li>Turn on the email digest. It goes to workspace members who have email delivery on, at the email hour.</li>
          <li>
            Connect Slack from the dashboard. The connection stores a channel, and the dashboard shows that channel
            name. Set a separate Slack delivery hour for the same digest.
          </li>
          <li>
            Optional: turn on the public link for a repository. Anyone can open that changelog without signing in, and
            they cannot see the rest of Git Digest.
          </li>
        </ol>
        <p className="text-muted-foreground text-base leading-relaxed">
          Digests are prepared for workspaces with an active subscription. The homepage Unlimited plan is $29 per
          month, or $25 per month on yearly billing, for the team rather than per seat. A lifetime option is $1,000
          once.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What the digest actually says</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Each change is a short summary, the author, and a link back to GitHub. When the change belongs to a pull
            request, the link uses that pull request number. Otherwise it uses a short commit id.
          </p>
          <p>
            Slack groups the lines under Feature, Bugfix, Improvement, Chore, and Refactoring. The header is the date
            for a daily digest, or the date range for a weekly one. Email shows the first five changes in each
            repository, with lines added and removed. If the window is empty, Slack says there were no code changes.
          </p>
          <p>
            An optional Friday leaderboard can also go out by email and Slack. It ranks lines changed for the week. It
            is a separate send from the commit digest, and you turn it on in the same delivery settings.
          </p>
        </div>
      </section>

      <section id="standup-vs-digest" className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Standup vs a commit digest</h2>
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[36rem] text-left text-sm">
            <caption className="bg-muted/40 text-muted-foreground px-4 py-3 text-left text-xs leading-relaxed">
              A digest reports committed work. Keep a note or a short meeting for work that never lands in git.
            </caption>
            <thead className="bg-muted/50 text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  <span className="sr-only">Topic</span>
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Live standup or form
                </th>
                <th scope="col" className="bg-golden-500/15 px-4 py-3 font-semibold">
                  GitHub activity digest
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.label} className="border-t">
                  <th scope="row" className="text-foreground px-4 py-3 font-medium">
                    {row.label}
                  </th>
                  <td className="text-muted-foreground px-4 py-3">{row.standup}</td>
                  <td className="bg-golden-500/10 text-foreground px-4 py-3">{row.digest}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">A public changelog you can open</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Repositories with the public link on are listed at{' '}
            <TextLink href={publicChangelogOwnerPath('sunergos-ro')}>/changelogs/sunergos-ro</TextLink>. Open one repo,
            such as{' '}
            <TextLink href={publicChangelogRepositoryPath('sunergos-ro', 'og-pilot')}>sunergos-ro/og-pilot</TextLink>,
            and you get the same kind of summary the digest is built from: one sentence per change, the author, and
            lines added and removed, grouped by month. Filters cover features, improvements, and bug fixes.
          </p>
          <p>
            That page is the public changelog, not the Slack message. Slack and email are the scheduled copy for the
            team. The public page is what you share when a repository should be readable without a login. Three
            annotated lines, a feature, a bugfix, and a refactoring, are on the{' '}
            <TextLink href={digestExamplesPath()}>digest examples</TextLink> page.
          </p>
        </div>
        <figure className="space-y-3">
          <img
            src="/images/replace-standups/og-pilot-entries.png"
            alt="Public changelog cards for sunergos-ro/og-pilot. Each card is a summarized code change with an author and line counts."
            className="w-full rounded-xl border"
            width={1200}
            height={900}
          />
          <figcaption className="text-muted-foreground text-xs leading-relaxed">
            Public changelog for sunergos-ro/og-pilot. The cards are summarized code changes, grouped under October
            2026.
          </figcaption>
        </figure>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">When not to drop the standup</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Use a digest when the engineering update is already in GitHub. Use a form, or keep a short live standup,
            when the work is hiring, support, design, or anything that never becomes a commit. The{' '}
            <TextLink href={campaignPath(standupBotsPath())}>standup bots comparison</TextLink> is the page for that
            choice. The <TextLink href={campaignPath(asyncStandupPath())}>async standup playbook</TextLink> covers
            cadence, blockers, and timezones if you still want a written update from people.
          </p>
          <p>
            An AI summary of one pull request is a different job. It helps a reviewer read that pull request, and it
            does not replace the team status. That comparison is on the{' '}
            <TextLink href={aiPrSummaryVsStandupPath()}>AI PR summary vs standup</TextLink> page.
          </p>
          <p>
            If the reason to switch is the cost of the meeting, the{' '}
            <TextLink href={campaignPath(dailyStandupCostPath())}>daily standup cost</TextLink> page prices a
            15-minute ritual, and the <TextLink href={campaignPath(meetingWastePath())}>meeting cost calculator</TextLink>{' '}
            takes your own salary and duration.
          </p>
          <p>
            Git Digest is not Gitingest. Gitingest turns a repository into text for an LLM. This product posts a team
            digest. The <TextLink href={campaignPath(githubDigestPath())}>GitHub digest comparison</TextLink> spells
            that out.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Replace standups FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            How the GitHub to Slack and email workflow works, and when a form is still the better fit.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Post the digest instead of collecting status</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub, pick the repositories, and send daily or weekly commit summaries to Slack and email.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={campaignPath(signInPath())}>
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
